import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Chip,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  useTheme
} from '@mui/material';
import {
  WalteroCard,
  WalteroButton,
  WalteroStatusChip,
  WalteroTypography
} from './waltero';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { WalteroReBarChart, WALTERO_CHART_COLORS, getChartColor } from './waltero';
import {
  SignalCellular4Bar as CellularIcon,
  SignalCellularConnectedNoInternet0Bar as NoSignalIcon,
  SignalCellular0Bar as InactiveIcon,
  SignalCellularAlt as ActiveIcon,

  NetworkCheck as NetworkIcon,
  History as HistoryIcon,
  Assessment as AssessmentIcon,
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Timeline as TimelineIcon,
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';

// Fix for default marker icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const CellularTab = ({ selectedOrgId }) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [overviewData, setOverviewData] = useState(null);
  const [simsData, setSimsData] = useState([]);

  const [selectedSim, setSelectedSim] = useState(null);
  const [simDetails, setSimDetails] = useState(null);
  const [trafficLogs, setTrafficLogs] = useState([]);
  const [aggregateTrafficLogs, setAggregateTrafficLogs] = useState([]);
  const [signallingLogs, setSignallingLogs] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [networkWhitelists, setNetworkWhitelists] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSimDetails, setShowSimDetails] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
    endDate: new Date()
  });
  const [connectionData, setConnectionData] = useState([]);


  // Tab definitions
  const tabs = [
    { label: 'Overview', icon: <AssessmentIcon /> },
    { label: 'SIM Cards', icon: <CellularIcon /> },
    { label: 'Traffic Logs', icon: <TimelineIcon /> },
    { label: 'Signaling Logs', icon: <NetworkIcon /> },
    { label: 'Network Whitelists', icon: <SecurityIcon /> },
  ];

  useEffect(() => {
    if (selectedOrgId) {
      loadCellularData();
    }
  }, [selectedOrgId, activeTab]);

  // Reload data when selectedSim changes on traffic logs or signaling logs tabs
  useEffect(() => {
    if (selectedOrgId && (activeTab === 2 || activeTab === 3)) { // Traffic Logs or Signaling Logs tab
      loadCellularData();
    }
  }, [selectedSim]);



  const loadCellularData = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('authToken');
      const headers = { Authorization: `Bearer ${token}` };

      // Load data based on active tab
      switch (activeTab) {
        case 0: // Overview
          const overviewResponse = await axios.get(
            `${API_BASE_URL}${API_ENDPOINTS.CELLULAR.OVERVIEW(selectedOrgId)}`,
            { headers }
          );
          setOverviewData(overviewResponse.data);
          break;

        case 1: // SIM Cards
          const simsResponse = await axios.get(
            `${API_BASE_URL}${API_ENDPOINTS.CELLULAR.SIMS(selectedOrgId)}`,
            { headers }
          );
          setSimsData(simsResponse.data.sims || []);
          break;

        case 2: // Traffic Logs
          // Always load organization aggregate traffic logs for the graph
          const aggregateResponse = await axios.get(
            `${API_BASE_URL}${API_ENDPOINTS.CELLULAR.TRAFFIC_LOGS(selectedOrgId)}`,
            { 
              headers,
              params: {
                startDate: dateRange.startDate.toISOString(),
                endDate: dateRange.endDate.toISOString(),
                limit: 100
              }
            }
          );
          setAggregateTrafficLogs(aggregateResponse.data.data?.trafficLogs || []);
          
          if (selectedSim) {
            // Load individual SIM traffic logs for the table
            const individualResponse = await axios.get(
              `${API_BASE_URL}${API_ENDPOINTS.CELLULAR.SIM_TRAFFIC_LOGS(selectedSim)}`,
              { 
                headers,
                params: {
                  startDate: dateRange.startDate.toISOString(),
                  endDate: dateRange.endDate.toISOString(),
                  limit: 100
                }
              }
            );
            setTrafficLogs(individualResponse.data.data?.trafficLogs || []);
          } else {
            // Use aggregate data for both graph and table
            setTrafficLogs(aggregateResponse.data.data?.trafficLogs || []);
          }
          break;

        case 3: // Signaling Logs
          // Load SIM data for dropdown if not already loaded
          if (simsData.length === 0) {
            const simsResponse = await axios.get(
              `${API_BASE_URL}${API_ENDPOINTS.CELLULAR.SIMS(selectedOrgId)}`,
              { headers }
            );
            setSimsData(simsResponse.data.sims || []);
          }

          if (selectedSim) {
            // Load signaling logs for selected SIM
            const signalingResponse = await axios.get(
              `${API_BASE_URL}${API_ENDPOINTS.CELLULAR.SIM_SIGNALLING_LOGS(selectedSim)}`,
              { 
                headers,
                params: {
                  limit: 100,
                  order: 'DESC',
                  timestamp: new Date().toISOString()
                }
              }
            );
            setSignallingLogs(signalingResponse.data.data?.docs || []);
          } else {
            setSignallingLogs([]);
          }
          break;

        case 4: // Network Whitelists
          const whitelistsResponse = await axios.get(
            `${API_BASE_URL}${API_ENDPOINTS.CELLULAR.NETWORK_WHITELISTS}`,
            { headers }
          );
          setNetworkWhitelists(whitelistsResponse.data.data?.whitelists || []);
          break;
      }
    } catch (err) {
      console.error('Error loading cellular data:', err);
      setError(err.response?.data?.error || 'Failed to load cellular data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'activated':
      case 'active':
        return 'success';
      case 'inactive':
      case 'deactivated':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'activated':
      case 'active':
        return <ActiveIcon />;
      case 'inactive':
      case 'deactivated':
        return <InactiveIcon />;
      case 'pending':
        return <NoSignalIcon />;
      default:
        return <CellularIcon />;
    }
  };

  const loadSimDetails = async (simId) => {
    try {
      // First try to find the SIM in the existing simsData (which has structured usage data)
      const existingSim = simsData.find(sim => sim.simId === simId);
      
      if (existingSim) {
        // Use the structured data from the SIMs list
        setSimDetails({ data: existingSim });
        setShowSimDetails(true);
        return;
      }

      // Fallback: load from API if not found in existing data
      const token = localStorage.getItem('authToken');
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.CELLULAR.SIM_DETAILS(simId)}`,
        { headers }
      );
      setSimDetails(response.data);
      setShowSimDetails(true);
    } catch (err) {
      console.error('Error loading SIM details:', err);
      setError(err.response?.data?.error || 'Failed to load SIM details');
    }
  };

  const searchSims = async () => {
    if (!searchTerm.trim()) return;

    try {
      const token = localStorage.getItem('authToken');
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.CELLULAR.SIM_SEARCH}`,
        { 
          headers,
          params: { search: searchTerm, limit: 50 }
        }
      );
      setSearchResults(response.data.results || []);
    } catch (err) {
      console.error('Error searching SIMs:', err);
      setError(err.response?.data?.error || 'Failed to search SIMs');
    }
  };



  // Helper function to validate coordinates
  const isValidCoordinate = (lat, lng) => {
    if (lat == null || lng == null) return false;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;
    return true;
  };

  // Render SIM locations map
  const renderSimLocationsMap = () => {
    if (!overviewData || !overviewData.sims) return null;

    const simsWithLocation = overviewData.sims.filter(sim => 
      sim.location && isValidCoordinate(sim.location.lat, sim.location.lng)
    );

    if (simsWithLocation.length === 0) {
      return (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <WalteroTypography variant="h6" color="textSecondary">
              No SIM locations available
            </WalteroTypography>
            <WalteroTypography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Location data requires Fleet tier or higher
            </WalteroTypography>
          </CardContent>
        </Card>
      );
    }

    // Calculate center point for the map
    const avgLat = simsWithLocation.reduce((sum, sim) => sum + sim.location.lat, 0) / simsWithLocation.length;
    const avgLng = simsWithLocation.reduce((sum, sim) => sum + sim.location.lng, 0) / simsWithLocation.length;

    return (
      <Card sx={{ width: '100%' }}>
        <CardContent sx={{ p: 2 }}>
          <WalteroTypography variant="h6" gutterBottom>
            SIM Locations ({simsWithLocation.length} SIMs)
          </WalteroTypography>
          <Box sx={{ height: 500, width: '100%', minWidth: '100%' }}>
            <MapContainer
              center={[avgLat, avgLng]}
              zoom={10}
              style={{ height: '100%', width: '100%', minWidth: '100%' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              {simsWithLocation.map((sim, index) => (
                <Marker
                  key={sim.simId || index}
                  position={[sim.location.lat, sim.location.lng]}
                >
                  <Popup>
                    <Box>
                      <WalteroTypography variant="subtitle2" gutterBottom>
                        {sim.deviceInfo?.serialNumber || sim.iccid}
                      </WalteroTypography>
                      <WalteroTypography variant="body2">
                        <strong>ICCID:</strong> {sim.iccid}
                      </WalteroTypography>
                      <WalteroTypography variant="body2">
                        <strong>Status:</strong> {sim.status}
                      </WalteroTypography>
                      <WalteroTypography variant="body2">
                        <strong>Network:</strong> {sim.network?.name || 'Unknown'}
                      </WalteroTypography>
                      <WalteroTypography variant="body2">
                        <strong>Usage:</strong> {formatBytes(sim.usage || 0)}
                      </WalteroTypography>
                      <WalteroTypography variant="body2">
                        <strong>Accuracy:</strong> {sim.location.accuracy}m
                      </WalteroTypography>
                    </Box>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderOverview = () => {
    if (!overviewData) return null;

    const { overview, sims } = overviewData;
    
    // Calculate median usage from detailed SIMs data
    const calculateMedianUsage = (usageType = 'lifetime') => {
      if (!sims || sims.length === 0) return 0;
      
      // Use the appropriate usage field
      const usageValues = sims
        .map(sim => {
          if (usageType === 'lifetime') {
            return sim.usage?.lifetime || 0;
          } else {
            return sim.usage?.period?.used || 0;
          }
        })
        .filter(usage => usage > 0)
        .sort((a, b) => a - b);
      
      if (usageValues.length === 0) return 0;
      
      const mid = Math.floor(usageValues.length / 2);
      if (usageValues.length % 2 === 0) {
        return (usageValues[mid - 1] + usageValues[mid]) / 2;
      } else {
        return usageValues[mid];
      }
    };
    
    const medianLifetimeUsage = calculateMedianUsage('lifetime');
    const medianPeriodUsage = calculateMedianUsage('period');

    return (
      <Box>
        <WalteroTypography variant="h4" gutterBottom color="primary">
          Cellular Overview
        </WalteroTypography>
        
        <Grid container spacing={3}>
          {/* Summary Cards */}
          <Grid item xs={12} sm={6} md={3}>
            <WalteroCard variant="elevated">
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <WalteroTypography variant="h6" gutterBottom color="primary">
                  Total SIMs
                </WalteroTypography>
                <WalteroTypography variant="h3" color="primary" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {overview.totalSims}
                </WalteroTypography>
                <WalteroTypography variant="body2" color="secondary">
                  {overview.activeSims} active
                </WalteroTypography>
              </Box>
            </WalteroCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <WalteroCard variant="elevated">
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <WalteroTypography variant="h6" gutterBottom color="primary">
                  Total Devices
                </WalteroTypography>
                <WalteroTypography variant="h3" color="primary" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {overview.totalDevices}
                </WalteroTypography>
                <WalteroTypography variant="body2" color="secondary">
                  {overview.devicesWithSims} with SIMs
                </WalteroTypography>
              </Box>
            </WalteroCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <WalteroCard variant="elevated">
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <WalteroTypography variant="h6" gutterBottom color="primary">
                  Lifetime Usage
                </WalteroTypography>
                <WalteroTypography variant="h3" sx={{ fontWeight: 'bold', mb: 1, color: theme.palette.waltero.secondary.primaryApricot }}>
                  {formatBytes(overview.dataUsage.lifetime.totalUsed)}
                </WalteroTypography>
                <WalteroTypography variant="body2" color="secondary">
                  {formatBytes(medianLifetimeUsage)} median per SIM
                </WalteroTypography>
              </Box>
            </WalteroCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <WalteroCard variant="elevated">
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <WalteroTypography variant="h6" gutterBottom color="primary">
                  Period Usage
                </WalteroTypography>
                {overview.dataUsage.period.periodType ? (
                  <>
                    <WalteroTypography variant="h3" sx={{ fontWeight: 'bold', mb: 1, color: theme.palette.waltero.primary.lightBlue }}>
                      {formatBytes(overview.dataUsage.period.totalUsed)}
                    </WalteroTypography>
                    <WalteroTypography variant="caption" color="secondary" sx={{ display: 'block', mb: 1 }}>
                      ({overview.dataUsage.period.periodType})
                    </WalteroTypography>
                  </>
                ) : (
                  <WalteroTypography variant="h4" color="secondary" sx={{ mb: 1 }}>
                    Not configured
                  </WalteroTypography>
                )}
                <WalteroTypography variant="body2" color="secondary">
                  {overview.dataUsage.period.periodType ? 
                    `${formatBytes(medianPeriodUsage)} median per SIM` : 
                    'Enable data_limit for period tracking'
                  }
                </WalteroTypography>
              </Box>
            </WalteroCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <WalteroTypography variant="h6" gutterBottom>
                  Networks
                </WalteroTypography>
                <WalteroTypography variant="h4" color="success.main">
                  {Object.keys(overview.networks).length}
                </WalteroTypography>
                <WalteroTypography variant="body2" color="textSecondary">
                  Different networks
                </WalteroTypography>
              </CardContent>
            </Card>
          </Grid>

          {/* Network Breakdown */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <WalteroTypography variant="h6" gutterBottom>
                  Network Distribution
                </WalteroTypography>
                <List>
                  {Object.entries(overview.networks).map(([network, count]) => (
                    <ListItem key={network}>
                      <ListItemText 
                        primary={network} 
                        secondary={`${count} SIMs`}
                      />
                      <Chip label={count} size="small" />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Status Breakdown */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <WalteroTypography variant="h6" gutterBottom>
                  Status Distribution
                </WalteroTypography>
                <List>
                  {Object.entries(overview.statusBreakdown).map(([status, count]) => (
                    <ListItem key={status}>
                      <ListItemIcon>
                        {getStatusIcon(status)}
                      </ListItemIcon>
                      <ListItemText 
                        primary={status} 
                        secondary={`${count} SIMs`}
                      />
                      <Chip 
                        label={count} 
                        size="small" 
                        color={getStatusColor(status)}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* SIM Locations Map */}
          <Grid item xs={12}>
            {renderSimLocationsMap()}
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderSims = () => {
    return (
      <Box>
        <WalteroTypography variant="h6" gutterBottom>
          SIM Cards ({simsData.length})
        </WalteroTypography>
        
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Serial Number</TableCell>
                <TableCell>ICCID</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Network</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>Data Usage</TableCell>
                <TableCell>Device</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {simsData.map((sim) => (
                <TableRow key={sim.simId} hover>
                  <TableCell sx={{ fontFamily: 'monospace' }}>
                    {sim.deviceInfo?.serialNumber || 'N/A'}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>
                    {sim.iccid}
                  </TableCell>
                  <TableCell>{sim.label || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(sim.status)}
                      label={sim.status}
                      color={getStatusColor(sim.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {sim.network?.name || 'Unknown'}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>
                    {sim.ip || 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <WalteroTypography variant="body2" fontWeight="bold">
                        {formatBytes(sim.usage || 0)}
                      </WalteroTypography>
                      <WalteroTypography variant="caption" color="textSecondary">
                        Lifetime
                      </WalteroTypography>
                      
                      {sim.data_limit?.type && (
                        <>
                          <WalteroTypography variant="body2" color="info.main" sx={{ mt: 0.5 }}>
                            {formatBytes(sim.data_limit.used || 0)}
                          </WalteroTypography>
                          <WalteroTypography variant="caption" color="textSecondary">
                            {sim.data_limit.type} period
                          </WalteroTypography>
                          
                          {sim.data_limit.total > 0 && (
                            <LinearProgress
                              variant="determinate"
                              value={((sim.data_limit.used || 0) / sim.data_limit.total) * 100}
                              sx={{ mt: 0.5, height: 4 }}
                              color="info"
                            />
                          )}
                        </>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {sim.deviceInfo ? (
                      <Box>
                        <WalteroTypography variant="body2" fontWeight="bold">
                          {sim.deviceInfo.serialNumber}
                        </WalteroTypography>
                        <WalteroTypography variant="caption" color="textSecondary">
                          DevEUI: {sim.deviceInfo.deveui}
                        </WalteroTypography>
                        <WalteroTypography variant="caption" color="textSecondary" display="block">
                          {sim.deviceInfo.deviceName}
                        </WalteroTypography>
                      </Box>
                    ) : (
                      'No device linked'
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton 
                        size="small" 
                        onClick={() => loadSimDetails(sim.simId)}
                      >
                        <SearchIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Summary Row */}
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <WalteroTypography variant="h6" color="primary">
                  Total SIMs: {simsData.length}
                </WalteroTypography>
              </Grid>
              <Grid item xs={12} md={3}>
                <WalteroTypography variant="h6" color="success.main">
                  Active: {simsData.filter(sim => sim.status === 'activated').length}
                </WalteroTypography>
              </Grid>
              <Grid item xs={12} md={3}>
                <WalteroTypography variant="h6" color="warning.main">
                  Lifetime Usage: {formatBytes(simsData.reduce((sum, sim) => sum + (sim.usage || 0), 0))}
                </WalteroTypography>
              </Grid>
              <Grid item xs={12} md={3}>
                <WalteroTypography variant="h6" color="info.main">
                  Period Usage: {formatBytes(simsData.reduce((sum, sim) => sum + (sim.data_limit?.used || 0), 0))}
                </WalteroTypography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderDataConsumption = () => {
    // REMOVED: Data Consumption tab - no longer used
    return null;
  };





  // Render daily connections bar graph
  const renderDailyConnectionsGraph = () => {
    if (!connectionData || connectionData.length === 0) {
      return (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <WalteroTypography variant="h6" color="textSecondary">
              No connection data available
            </WalteroTypography>
          </CardContent>
        </Card>
      );
    }

    // Check if this is individual SIM data or aggregate data
    const isIndividualSim = connectionData.length > 0 && connectionData[0].simId;
    const simLabel = isIndividualSim ? connectionData[0].simLabel : null;
    
    const title = isIndividualSim 
      ? `Daily Connections for ${simLabel || 'Selected SIM'} (Last 30 Days)`
      : 'Daily SIM Connections (Last 30 Days)';
    
    const yAxisLabel = isIndividualSim ? 'Connected (1) / Not Connected (0)' : 'Number of SIMs';
    const tooltipLabel = isIndividualSim ? 'Connection Status' : 'SIMs Connected';

    return (
      <Card>
        <CardContent>
          <WalteroTypography variant="h6" gutterBottom>
            {title}
          </WalteroTypography>
          <WalteroReBarChart
            data={connectionData}
            height={300}
            width="100%"
            xAxisKey="day"
            yAxisKeys={['connections']}
            colors={[isIndividualSim ? getChartColor(2, WALTERO_CHART_COLORS) : getChartColor(0, WALTERO_CHART_COLORS)]}
            showGrid={true}
            showTooltip={true}
            showLegend={false}
            tooltipFormatter={(value, name) => [
              isIndividualSim ? (value === 1 ? 'Connected' : 'Not Connected') : value, 
              tooltipLabel
            ]}
            labelFormatter={(label) => {
              const item = connectionData.find(d => d.day === label);
              return item ? `${item.day} (${item.date})` : label;
            }}
            barProps={{
              name: tooltipLabel,
              radius: [4, 4, 0, 0]
            }}
          />
        </CardContent>
      </Card>
    );
  };

  const renderDataTransferGraph = () => {
    // Always show aggregate data in the graph
    if (!aggregateTrafficLogs || aggregateTrafficLogs.length === 0) {
      return (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <WalteroTypography variant="h6" color="textSecondary">
              Daily Data Transfer
            </WalteroTypography>
            <WalteroTypography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Loading organization traffic data...
            </WalteroTypography>
          </CardContent>
        </Card>
      );
    }

    // Create a full dataset for the selected date range
    const dailyData = {};
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    
    // Calculate the number of days in the range
    const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    
    // Initialize all days in the range with zeros
    for (let i = 0; i <= daysDiff; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      const dayKey = date.toISOString().split('T')[0]; // YYYY-MM-DD format
      const dayLabel = date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
      
      dailyData[dayKey] = {
        day: dayLabel,
        date: dayKey,
        totalBytes: 0,
        bytesRx: 0,
        bytesTx: 0,
        sessions: 0
      };
    }
    
    // Now populate with actual traffic log data (filter out empty entries)
    aggregateTrafficLogs
      .filter(log => log.total_bytes && log.total_bytes > 0) // Filter out empty entries
      .forEach(log => {
        const date = new Date(log.timestamp);
        const dayKey = date.toISOString().split('T')[0]; // YYYY-MM-DD format
        
        // Only include data within the selected date range
        if (dailyData[dayKey] && date >= startDate && date <= endDate) {
          dailyData[dayKey].totalBytes += log.total_bytes || 0;
          dailyData[dayKey].bytesRx += log.bytes_rx || 0;
          dailyData[dayKey].bytesTx += log.bytes_tx || 0;
          dailyData[dayKey].sessions += 1;
        }
      });

    // Convert to array and sort by date (already in chronological order)
    const chartData = Object.values(dailyData).sort((a, b) => new Date(a.date) - new Date(b.date));

    // Always show organization aggregate in the graph
    const simLabel = 'Organization Aggregate';

    // Format date range for display
    const formatDateRange = () => {
      const start = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const end = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return `${start} - ${end}`;
    };

    return (
      <Card>
        <CardContent>
          <WalteroTypography variant="h6" gutterBottom>
            Daily Data Transfer for {simLabel} ({formatDateRange()})
          </WalteroTypography>
          <WalteroReBarChart
            data={chartData}
            height={300}
            width="100%"
            xAxisKey="day"
            yAxisKeys={['totalBytes']}
            colors={[getChartColor(1, WALTERO_CHART_COLORS)]}
            showGrid={true}
            showTooltip={true}
            showLegend={false}
            tooltipFormatter={(value, name) => {
              if (name === 'totalBytes') return [`${formatBytes(value)}`, 'Total Data'];
              if (name === 'bytesRx') return [`${formatBytes(value)}`, 'Data Received'];
              if (name === 'bytesTx') return [`${formatBytes(value)}`, 'Data Sent'];
              return [value, name];
            }}
            labelFormatter={(label) => {
              const item = chartData.find(d => d.day === label);
              return item ? `${item.day} (${item.date})` : label;
            }}
            barProps={{
              name: 'totalBytes',
              radius: [4, 4, 0, 0]
            }}
          />
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, backgroundColor: theme.palette.waltero.primary.lightBlue, borderRadius: 1 }} />
              <WalteroTypography variant="body2">Total Data Transfer</WalteroTypography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderTrafficLogs = () => {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <WalteroTypography variant="h6">
            Traffic Logs
          </WalteroTypography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Select SIM</InputLabel>
              <Select
                value={selectedSim || ''}
                onChange={(e) => setSelectedSim(e.target.value)}
                label="Select SIM"
              >
                {simsData.map((sim) => (
                  <MenuItem key={sim.simId} value={sim.simId}>
                    {sim.deviceInfo?.serialNumber || sim.iccid} - {sim.label || 'No Label'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedSim && (
              <>
                <TextField
                  label="Start Date"
                  type="datetime-local"
                  value={dateRange.startDate.toISOString().slice(0, 16)}
                  onChange={(e) => setDateRange(prev => ({ 
                    ...prev, 
                    startDate: new Date(e.target.value) 
                  }))}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="End Date"
                  type="datetime-local"
                  value={dateRange.endDate.toISOString().slice(0, 16)}
                  onChange={(e) => setDateRange(prev => ({ 
                    ...prev, 
                    endDate: new Date(e.target.value) 
                  }))}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
                <WalteroButton 
                  variant="contained" 
                  onClick={loadCellularData}
                  startIcon={<SearchIcon />}
                >
                  Refresh
                </WalteroButton>
              </>
            )}
          </Box>
        </Box>

        {/* Daily Connections Graph */}
        <Box sx={{ mb: 3 }}>
          {renderDailyConnectionsGraph()}
        </Box>

        {/* Data Transfer Graph - Always show aggregate */}
        <Box sx={{ mb: 3 }}>
          {renderDataTransferGraph()}
        </Box>

        {!selectedSim ? (
          <>
            {/* Landing page with aggregate data */}
            <Box sx={{ mb: 3 }}>
              <Card>
                <CardContent>
                  <WalteroTypography variant="h6" gutterBottom>
                    Organization Traffic Overview
                  </WalteroTypography>
                  <WalteroTypography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Showing aggregate traffic data for all SIMs in this organization
                  </WalteroTypography>
                  
                  {aggregateTrafficLogs.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <WalteroTypography variant="h6" color="textSecondary">
                        No traffic data available for the selected period
                      </WalteroTypography>
                      <WalteroTypography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        Try adjusting the date range or check if any SIMs have been active
                      </WalteroTypography>
                    </Box>
                  ) : (
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Timestamp</TableCell>
                            <TableCell>SIM</TableCell>
                            <TableCell>Total Bytes</TableCell>
                            <TableCell>Network Type</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {aggregateTrafficLogs
                            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                            .slice(0, 20) // Show only first 20 entries
                            .map((log, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                {new Date(log.timestamp).toLocaleString()}
                              </TableCell>
                              <TableCell>
                                {log.sim_label || log.sim_iccid || 'Unknown SIM'}
                              </TableCell>
                              <TableCell>{formatBytes(log.total_bytes)}</TableCell>
                              <TableCell>{log.network_type || 'N/A'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Box>
            
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <WalteroTypography variant="h6" color="textSecondary">
                Select a SIM to view detailed traffic logs
              </WalteroTypography>
            </Box>
          </>
        ) : (
          <>
            {trafficLogs.length === 0 ? (
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <WalteroTypography variant="h6" color="textSecondary">
                    No traffic logs found for the selected period
                  </WalteroTypography>
                  <WalteroTypography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Try adjusting the date range or check if the SIM has been active
                  </WalteroTypography>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Timestamp</TableCell>
                          <TableCell>Total Bytes</TableCell>
                          <TableCell>Network Type</TableCell>
                          <TableCell>Country Code</TableCell>
                          <TableCell>Session ID</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {trafficLogs
                          .filter(log => log.total_bytes && log.total_bytes > 0) // Filter out empty entries
                          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                          .map((log, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              {new Date(log.timestamp).toLocaleString()}
                            </TableCell>
                            <TableCell>{formatBytes(log.total_bytes)}</TableCell>
                            <TableCell>{log.network_type || 'N/A'}</TableCell>
                            <TableCell>{log.country_code || 'N/A'}</TableCell>
                            <TableCell>{log.session_id || 'N/A'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </Box>
    );
  };

  const renderSignallingLogs = () => {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <WalteroTypography variant="h6">
            Signaling Logs
          </WalteroTypography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Select SIM</InputLabel>
              <Select
                value={selectedSim || ''}
                onChange={(e) => setSelectedSim(e.target.value)}
                label="Select SIM"
              >
                {simsData.map((sim) => (
                  <MenuItem key={sim.simId} value={sim.simId}>
                    {sim.deviceInfo?.serialNumber || sim.iccid} - {sim.label || 'No Label'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedSim && (
              <WalteroButton 
                variant="contained" 
                onClick={loadCellularData}
                startIcon={<SearchIcon />}
              >
                Refresh
              </WalteroButton>
            )}
          </Box>
        </Box>

        {!selectedSim ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <WalteroTypography variant="h6" color="textSecondary">
              Select a SIM to view signaling logs
            </WalteroTypography>
          </Box>
        ) : (
          <>
            {signallingLogs.length === 0 ? (
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <WalteroTypography variant="h6" color="textSecondary">
                    No signaling logs found
                  </WalteroTypography>
                  <WalteroTypography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    This SIM may not have recent signaling activity
                  </WalteroTypography>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent>
                  <WalteroTypography variant="h6" gutterBottom>
                    Signaling Logs ({signallingLogs.length})
                  </WalteroTypography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Timestamp</TableCell>
                          <TableCell>Protocol</TableCell>
                          <TableCell>Info</TableCell>
                          <TableCell>Packet (First 100 chars)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {signallingLogs.map((log, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              {new Date(log.timestamp * 1000).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={log.protocol}
                                color={
                                  log.protocol === 'DIAMETER' ? 'primary' : 
                                  log.protocol === 'GTPv2' ? 'secondary' : 
                                  log.protocol === 'GSM MAP' ? 'success' : 'default'
                                }
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <WalteroTypography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {log.info}
                              </WalteroTypography>
                            </TableCell>
                            <TableCell>
                              <WalteroTypography 
                                variant="body2" 
                                sx={{ 
                                  fontFamily: 'monospace', 
                                  fontSize: '0.75rem',
                                  wordBreak: 'break-all'
                                }}
                              >
                                {log.packet?.substring(0, 100)}...
                              </WalteroTypography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </Box>
    );
  };

  const renderNetworkWhitelists = () => {
    return (
      <Box>
        <WalteroTypography variant="h6" gutterBottom>
          Network Whitelists ({networkWhitelists.length})
        </WalteroTypography>
        
        <Grid container spacing={2}>
          {networkWhitelists.map((whitelist, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card>
                <CardContent>
                  <WalteroTypography variant="h6" gutterBottom>
                    {whitelist.name}
                  </WalteroTypography>
                  <WalteroTypography variant="body2" color="textSecondary">
                    {whitelist.description || 'No description'}
                  </WalteroTypography>
                  {whitelist.entries && (
                    <Box sx={{ mt: 2 }}>
                      <WalteroTypography variant="subtitle2" gutterBottom>
                        Entries:
                      </WalteroTypography>
                      <List dense>
                        {whitelist.entries.map((entry, idx) => (
                          <ListItem key={idx}>
                            <ListItemText 
                              primary={`${entry.mcc}/${entry.mnc}`}
                              secondary={entry.description || 'No description'}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return renderOverview();
      case 1:
        return renderSims();
      case 2:
        return renderTrafficLogs();
      case 3:
        return renderSignallingLogs();
      case 4:
        return renderNetworkWhitelists();
      default:
        return null;
    }
  };

  // SIM Details Dialog
  const renderSimDetailsDialog = () => {
    if (!simDetails) return null;

    const sim = simDetails.data;

    return (
      <Dialog 
        open={showSimDetails} 
        onClose={() => setShowSimDetails(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <WalteroTypography variant="h6">
              SIM Details - {sim?.id}
            </WalteroTypography>
            <IconButton onClick={() => setShowSimDetails(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            {/* Basic SIM Info */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <WalteroTypography variant="h6" gutterBottom color="primary">
                    Basic Information
                  </WalteroTypography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="SIM ID" 
                        secondary={<WalteroTypography sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{sim?.id}</WalteroTypography>}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Status" 
                        secondary={
                          <Chip
                            label={sim?.online ? 'Online' : 'Offline'}
                            color={sim?.online ? 'success' : 'default'}
                            size="small"
                          />
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Label" 
                        secondary={sim?.label || 'N/A'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Activated" 
                        secondary={
                          <Chip
                            icon={getStatusIcon(sim?.activated ? 'activated' : 'inactive')}
                            label={sim?.activated ? 'Yes' : 'No'}
                            color={getStatusColor(sim?.activated ? 'activated' : 'inactive')}
                            size="small"
                          />
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Type" 
                        secondary={sim?.is_softsim ? 'SoftSIM' : 'Physical'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Network List" 
                        secondary={sim?.network_whitelist || 'N/A'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Connector" 
                        secondary={sim?.connector || 'N/A'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Last Came Online At" 
                        secondary={sim?.last_came_online_at ? new Date(sim.last_came_online_at).toLocaleString() : 'N/A'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="IPv4" 
                        secondary={<WalteroTypography sx={{ fontFamily: 'monospace' }}>{sim?.ipv4 || 'N/A'}</WalteroTypography>}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Technical Identifiers */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <WalteroTypography variant="h6" gutterBottom color="primary">
                    Technical Identifiers
                  </WalteroTypography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="ICCID" 
                        secondary={<WalteroTypography sx={{ fontFamily: 'monospace' }}>{sim?.iccid}</WalteroTypography>}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="IMSI" 
                        secondary={<WalteroTypography sx={{ fontFamily: 'monospace' }}>{sim?.imsi}</WalteroTypography>}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="MSISDN" 
                        secondary={<WalteroTypography sx={{ fontFamily: 'monospace' }}>{sim?.msisdn}</WalteroTypography>}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="IMEI" 
                        secondary={<WalteroTypography sx={{ fontFamily: 'monospace' }}>{sim?.imei || 'N/A'}</WalteroTypography>}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="IMEI Lock" 
                        secondary={sim?.imei_lock || 'N/A'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Country" 
                        secondary={sim?.network?.country_code || 'N/A'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Network" 
                        secondary={sim?.network?.name || 'N/A'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="MCC" 
                        secondary={<WalteroTypography sx={{ fontFamily: 'monospace' }}>{sim?.network?.mcc || 'N/A'}</WalteroTypography>}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="MNC" 
                        secondary={<WalteroTypography sx={{ fontFamily: 'monospace' }}>{sim?.network?.mnc || 'N/A'}</WalteroTypography>}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Usage & Data Limit */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <WalteroTypography variant="h6" gutterBottom color="primary">
                    Usage Information
                  </WalteroTypography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Lifetime Usage" 
                        secondary={<WalteroTypography variant="h6" color="warning.main">{formatBytes(sim?.usage || 0)}</WalteroTypography>}
                      />
                    </ListItem>
                    {/* Use structured usage data if available, otherwise fallback to raw data_limit */}
                    {(sim?.usage?.period?.type || sim?.data_limit) && (
                      <>
                        <ListItem>
                          <ListItemText 
                            primary="Usage for Period" 
                            secondary={<WalteroTypography variant="h6" color="info.main">
                              {formatBytes(sim?.data_limit?.used || 0)}
                            </WalteroTypography>}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Limit" 
                            secondary={<WalteroTypography variant="h6" color="success.main">
                              {formatBytes(sim?.data_limit?.total || 0)}
                            </WalteroTypography>}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Usage Progress" 
                            secondary={
                              <Box>
                                {(() => {
                                  const used = sim?.data_limit?.used || 0;
                                  const limit = sim?.data_limit?.total || 1;
                                  const percentage = (used / limit) * 100;
                                  return (
                                    <>
                                      <WalteroTypography variant="body2" color="textSecondary">
                                        {formatBytes(used)} / {formatBytes(limit)} ({percentage.toFixed(1)}%)
                                      </WalteroTypography>
                                      <LinearProgress
                                        variant="determinate"
                                        value={percentage}
                                        sx={{ mt: 1, height: 8 }}
                                        color="info"
                                      />
                                    </>
                                  );
                                })()}
                              </Box>
                            }
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Usage Period" 
                            secondary={(() => {
                              const type = sim?.data_limit?.type;
                              return type ? type.charAt(0).toUpperCase() + type.slice(1) : 'N/A';
                            })()}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Current Period" 
                            secondary={sim?.data_limit?.period || 'N/A'}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Alert Threshold" 
                            secondary={formatBytes(sim?.data_limit?.alert_threshold || 0)}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Resets At" 
                            secondary={sim?.data_limit?.resets_at ? new Date(sim.data_limit.resets_at).toLocaleString() : 'N/A'}
                          />
                        </ListItem>
                      </>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Location Info */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <WalteroTypography variant="h6" gutterBottom color="primary">
                    Location Information
                  </WalteroTypography>
                  {sim?.location ? (
                    <Box>
                      <List dense>
                        <ListItem>
                          <ListItemText 
                            primary="Latitude" 
                            secondary={<WalteroTypography sx={{ fontFamily: 'monospace' }}>{sim.location.lat}</WalteroTypography>}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Longitude" 
                            secondary={<WalteroTypography sx={{ fontFamily: 'monospace' }}>{sim.location.lng}</WalteroTypography>}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Accuracy" 
                            secondary={`${sim.location.accuracy}m`}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Cell ID" 
                            secondary={<WalteroTypography sx={{ fontFamily: 'monospace' }}>{sim.location.cell_id}</WalteroTypography>}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Location Area Code (LAC)" 
                            secondary={sim.location.location_area_code || 'N/A'}
                          />
                        </ListItem>
                      </List>
                      
                      {/* Location Map */}
                      <Box sx={{ mt: 2, height: 200, border: `1px solid ${theme.palette.waltero.neutral.mediumGray}`, borderRadius: 1 }}>
                        <MapContainer 
                          center={[sim.location.lat, sim.location.lng]} 
                          zoom={13} 
                          style={{ height: '100%', width: '100%' }}
                        >
                          <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          />
                          <Marker 
                            position={[sim.location.lat, sim.location.lng]}
                            icon={L.divIcon({
                              html: `<div style="background-color: ${theme.palette.waltero.primary.lightBlue}; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 0 2px ${theme.palette.waltero.primary.lightBlue};"></div>`,
                              className: 'custom-marker',
                              iconSize: [16, 16],
                              iconAnchor: [8, 8]
                            })}
                          >
                            <Popup>
                              <Box>
                                <WalteroTypography variant="subtitle2" fontWeight="bold">
                                  SIM Location
                                </WalteroTypography>
                                <WalteroTypography variant="body2">
                                  SIM ID: {sim.id}
                                </WalteroTypography>
                                <WalteroTypography variant="body2">
                                  Network: {sim.network?.name}
                                </WalteroTypography>
                                <WalteroTypography variant="body2">
                                  Accuracy: {sim.location.accuracy}m
                                </WalteroTypography>
                                <WalteroTypography variant="body2">
                                  Cell ID: {sim.location.cell_id}
                                </WalteroTypography>
                              </Box>
                            </Popup>
                          </Marker>
                        </MapContainer>
                      </Box>
                    </Box>
                  ) : (
                    <WalteroTypography variant="body2" color="textSecondary">
                      No location information available
                    </WalteroTypography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Technologies */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <WalteroTypography variant="h6" gutterBottom color="primary">
                    Enabled Technologies
                  </WalteroTypography>
                  {sim?.technologies ? (
                    <Box>
                      <WalteroTypography variant="body1" sx={{ mb: 1 }}>
                        {[
                          sim.technologies['2g_3g'] ? '2G/3G' : null,
                          sim.technologies['4g'] ? '4G' : null,
                          sim.technologies.sms ? 'SMS' : null
                        ].filter(Boolean).join('/') || 'None'}
                      </WalteroTypography>
                      <List dense>
                        <ListItem>
                          <ListItemText 
                            primary="SMS" 
                            secondary={
                              <Chip
                                label={sim.technologies.sms ? 'Enabled' : 'Disabled'}
                                color={sim.technologies.sms ? 'success' : 'default'}
                                size="small"
                              />
                            }
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="2G/3G" 
                            secondary={
                              <Chip
                                label={sim.technologies['2g_3g'] ? 'Enabled' : 'Disabled'}
                                color={sim.technologies['2g_3g'] ? 'success' : 'default'}
                                size="small"
                              />
                            }
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="4G" 
                            secondary={
                              <Chip
                                label={sim.technologies['4g'] ? 'Enabled' : 'Disabled'}
                                color={sim.technologies['4g'] ? 'success' : 'default'}
                                size="small"
                              />
                            }
                          />
                        </ListItem>
                      </List>
                    </Box>
                  ) : (
                    <WalteroTypography variant="body2" color="textSecondary">
                      No technology information available
                    </WalteroTypography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Tags */}
            {sim?.tags && sim.tags.length > 0 && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <WalteroTypography variant="h6" gutterBottom color="primary">
                      Tags ({sim.tags.length})
                    </WalteroTypography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {sim.tags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag.name}
                          sx={{ 
                            backgroundColor: tag.color,
                            color: 'white',
                            '&:hover': {
                              backgroundColor: tag.color,
                              filter: 'brightness(0.9)'
                            }
                          }}
                          size="small"
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Box>
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Content */}
      {!loading && !error && renderTabContent()}

      {/* SIM Details Dialog */}
      {renderSimDetailsDialog()}
    </Box>
  );
};

export default CellularTab; 